import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Slider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Snackbar,
  Divider,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  VpnKey as KeyIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import API_CONFIG from '../../config/api';
import { ADVANCED_AI_ESCALATION_COPY, formatEscalationPremiumCaption } from './advancedConfigurationCopy';
import AutomationRepoConfigSection from './AutomationRepoConfigSection';
import AutomationKnowledgePullSection from './AutomationKnowledgePullSection';
import {
  DEFAULT_AUTOMATION_REPO_CONFIG,
  LEGACY_AUTOMATION_REPO_CONFIG_KEYS,
  buildRepoConfigUpsertBody,
  mapRepoConfigResponse,
} from './automationRepoConfigUtils';
import {
  clearStratovaiSession,
  createSettingsApiError,
  getTokenErrorMessage,
  isStratovaiSessionExpired,
} from './settingsTokenErrorUtils';

const ConfigurationSettings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [hasChanges, setHasChanges] = useState(false);
  const [tabChanges, setTabChanges] = useState({});
  const [automationClearFlags, setAutomationClearFlags] = useState({
    clearPassword: false,
    clearUsername: false,
  });
  const [environments, setEnvironments] = useState([]);
  const [showKeys, setShowKeys] = useState({
    ai_api_key: false,
    jira_api_token: false,
    figma_api_token: false
  });
  // Local state for API key inputs - always empty on load
  const [apiKeyInputs, setApiKeyInputs] = useState({
    ai_api_key: '',
    jira_api_token: '',
    figma_api_token: ''
  });
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    display_name: '',
    description: '',
    aliases: ''
  });
  const [automationRepoConfig, setAutomationRepoConfig] = useState({ ...DEFAULT_AUTOMATION_REPO_CONFIG });
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [repoTokenInput, setRepoTokenInput] = useState('');
  const [repoConfigChanged, setRepoConfigChanged] = useState(false);
  const [frameworkSettingsStatus, setFrameworkSettingsStatus] = useState(null);

  const reportSettingsError = useCallback((err, { unsavedChanges = false } = {}) => {
    const message = getTokenErrorMessage(err, { unsavedChanges });
    if (isStratovaiSessionExpired(err)) {
      clearStratovaiSession();
    }
    setError(message);
    setSnackbar({ open: true, message, severity: 'error' });
  }, []);

  const fetchModules = useCallback(async () => {
    setLoadingModules(true);
    try {
      const token = localStorage.getItem('stratovai_access_token');
      const selectedProjectStr = localStorage.getItem('selectedProject');
      const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
      const projectId = selectedProject?.project_id || selectedProject?.id;
      
      if (!projectId) {
        return;
      }
      
      const modulesUrl = `${API_CONFIG.BASE_URL}/api/module-taxonomy/${projectId}/modules?active_only=false`;
      const response = await fetch(modulesUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const modulesData = await response.json();
        setModules(modulesData);
      }
    } catch (err) {
      console.error('Failed to fetch modules:', err);
    } finally {
      setLoadingModules(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('stratovai_access_token');
      
      // Get selected project from localStorage - project_id is now required
      const selectedProjectStr = localStorage.getItem('selectedProject');
      const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
      const projectId = selectedProject?.project_id || selectedProject?.id;
      
      if (!projectId) {
        throw new Error('No project selected. Please select a project first.');
      }
      setCurrentProjectId(projectId);
      
      // Fetch config - project_id is required for project-scoped configuration
      const configUrl = `${API_CONFIG.BASE_URL}/api/config?project_id=${encodeURIComponent(projectId)}`;
      const configResponse = await fetch(configUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!configResponse.ok) {
        // Check if response is JSON or HTML
        const contentType = configResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await configResponse.json();
          throw createSettingsApiError(
            configResponse.status,
            errorData.detail,
            `Failed to fetch configuration: ${configResponse.status}`,
          );
        } else {
          // HTML error page - read and discard to clear response body
          await configResponse.text();
          throw new Error(`Server error (${configResponse.status}): ${configResponse.statusText}`);
        }
      }
      
      // Verify response is JSON before parsing
      const contentType = configResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response. Please check API endpoint.');
      }
      
      const configData = await configResponse.json();
      setConfig(configData.config);
      setOriginalConfig(configData.config);

      const repoConfigResponse = await fetch(
        `${API_CONFIG.BASE_URL}/automation/v1/repo-config?project_id=${encodeURIComponent(projectId)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (repoConfigResponse.ok) {
        const repoConfigData = await repoConfigResponse.json();
        const mapped = mapRepoConfigResponse(repoConfigData);
        setAutomationRepoConfig(mapped);
      } else {
        setAutomationRepoConfig({ ...DEFAULT_AUTOMATION_REPO_CONFIG });
      }
      setRepoTokenInput('');
      setRepoConfigChanged(false);

      const frameworkStatusResponse = await fetch(
        `${API_CONFIG.BASE_URL}/automation/v1/profile-settings/status?project_id=${encodeURIComponent(projectId)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (frameworkStatusResponse.ok) {
        setFrameworkSettingsStatus(await frameworkStatusResponse.json());
      } else {
        setFrameworkSettingsStatus(null);
      }
      
      // Reset API key inputs to empty (never load full keys)
      setApiKeyInputs({
        ai_api_key: '',
        jira_api_token: '',
        figma_api_token: ''
      });
      
      // Fetch schema
      const schemaResponse = await fetch(`${API_CONFIG.BASE_URL}/api/config/schema`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!schemaResponse.ok) {
        // Check if response is JSON or HTML
        const contentType = schemaResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await schemaResponse.json();
          throw createSettingsApiError(
            schemaResponse.status,
            errorData.detail,
            `Failed to fetch schema: ${schemaResponse.status}`,
          );
        } else {
          // HTML error page - read and discard to clear response body
          await schemaResponse.text();
          throw new Error(`Server error (${schemaResponse.status}): ${schemaResponse.statusText}`);
        }
      }
      
      // Verify response is JSON before parsing
      const schemaContentType = schemaResponse.headers.get('content-type');
      if (!schemaContentType || !schemaContentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response for schema. Please check API endpoint.');
      }
      
      const schemaData = await schemaResponse.json();
      setSchema(schemaData.config_schema);
    } catch (err) {
      console.error('Configuration fetch error:', err);
      const errorMessage = getTokenErrorMessage(err) || 'Failed to load configuration';
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      if (isStratovaiSessionExpired(err)) {
        clearStratovaiSession();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    // Initialize with default environments
    setEnvironments([
      { id: 1, name: 'Development', base_url: 'https://dev.example.com', app_id: '', active: true },
      { id: 2, name: 'QA', base_url: 'https://qa.example.com', app_id: '', active: true },
      { id: 3, name: 'Staging', base_url: 'https://staging.example.com', app_id: '', active: true },
      { id: 4, name: 'Production', base_url: 'https://example.com', app_id: '', active: false }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch modules when config is loaded and we're on the module taxonomy tab
    if (config && activeTab === 7) {
      fetchModules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, activeTab]);

  useEffect(() => {
    if (config && originalConfig) {
      const changed = JSON.stringify(config) !== JSON.stringify(originalConfig);
      setHasChanges(changed);
    }
  }, [config, originalConfig]);

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    // Track changes per tab
    const tabKey = getTabKeyFromConfigKey(key);
    setTabChanges(prev => ({ ...prev, [tabKey]: true }));
  };

  const handleRepoConfigChange = (field, value) => {
    setAutomationRepoConfig((prev) => ({ ...prev, [field]: value }));
    setRepoConfigChanged(true);
    setTabChanges((prev) => ({ ...prev, automation: true }));
  };

  const handleRepoTokenInputChange = (value) => {
    setRepoTokenInput(value);
    if (value.trim()) {
      setRepoConfigChanged(true);
      setTabChanges((prev) => ({ ...prev, automation: true }));
    }
  };

  const fetchAutomationRepoConfig = async (projectId, authToken) => {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/automation/v1/repo-config?project_id=${encodeURIComponent(projectId)}`,
      { headers: { 'Authorization': `Bearer ${authToken}` } }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createSettingsApiError(
        response.status,
        errorData.detail,
        'Failed to load GitHub PR repository configuration',
      );
    }
    return mapRepoConfigResponse(await response.json());
  };

  const saveAutomationRepoConfig = async (projectId, authToken) => {
    const body = buildRepoConfigUpsertBody({
      projectId,
      repoConfig: automationRepoConfig,
      tokenInput: repoTokenInput,
    });
    const putResponse = await fetch(`${API_CONFIG.BASE_URL}/automation/v1/repo-config`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(body),
    });
    if (!putResponse.ok) {
      const errorData = await putResponse.json().catch(() => ({}));
      throw createSettingsApiError(
        putResponse.status,
        errorData.detail,
        'Failed to save GitHub PR repository configuration',
      );
    }
    const confirmed = await fetchAutomationRepoConfig(projectId, authToken);
    setAutomationRepoConfig(confirmed);
    setRepoTokenInput('');
    setRepoConfigChanged(false);
    return confirmed;
  };

  const getTabKeyFromConfigKey = (key) => {
    if (key.startsWith('project.')) return 'project-basics';
    if (key.startsWith('application.')) return 'application-profile';
    if (key.startsWith('automation.')) return 'automation';
    if (key.startsWith('excel.')) return 'excel-import';
    if (key.startsWith('tokens.')) return 'tokens-security';
    if (key.startsWith('module_taxonomy.')) return 'module-taxonomy';
    return 'advanced';
  };

  const handleTabSave = async (tabKey) => {
    const automationRepoPending = tabKey === 'automation' && repoConfigChanged;
    if (!tabChanges[tabKey] && !automationRepoPending) {
      setSnackbar({ open: true, message: 'No changes to save', severity: 'info' });
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      const updates = {};
      Object.keys(config).forEach(key => {
        if (key.startsWith('automation.runtime.has_') || key.endsWith('_suffix')) {
          return;
        }
        if (LEGACY_AUTOMATION_REPO_CONFIG_KEYS.has(key)) {
          return;
        }
        const keyTab = getTabKeyFromConfigKey(key);
        if (keyTab === tabKey && config[key] !== originalConfig[key]) {
          updates[key] = config[key];
        }
      });
      
      const token = localStorage.getItem('stratovai_access_token');
      // Get selected project - project_id is required
      const selectedProjectStr = localStorage.getItem('selectedProject');
      const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
      const projectId = selectedProject?.project_id || selectedProject?.id;
      
      if (!projectId) {
        throw new Error('No project selected. Please select a project first.');
      }

      if (automationRepoPending) {
        await saveAutomationRepoConfig(projectId, token);
      }

      const hasProjectConfigUpdates = Object.keys(updates).length > 0
        || (tabKey === 'automation' && (automationClearFlags.clearPassword || automationClearFlags.clearUsername));

      if (!hasProjectConfigUpdates) {
        setTabChanges(prev => ({ ...prev, [tabKey]: false }));
        setAutomationClearFlags({ clearPassword: false, clearUsername: false });
        setSnackbar({ open: true, message: `${getTabDisplayName(tabKey)} saved successfully!`, severity: 'success' });
        return;
      }

      if (Object.keys(updates).length === 0 && !(tabKey === 'automation' && (automationClearFlags.clearPassword || automationClearFlags.clearUsername))) {
        setSnackbar({ open: true, message: 'No changes to save', severity: 'info' });
        return;
      }
      
      const configUrl = `${API_CONFIG.BASE_URL}/api/config?project_id=${encodeURIComponent(projectId)}`;
      const payload = { updates };
      if (tabKey === 'automation') {
        if (automationClearFlags.clearPassword) {
          payload.clear_automation_password = true;
        }
        if (automationClearFlags.clearUsername) {
          payload.clear_automation_username = true;
        }
      }

      const response = await fetch(configUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw createSettingsApiError(response.status, errorData.detail, 'Failed to save configuration');
      }
      
      const data = await response.json();
      setConfig(data.config);
      setOriginalConfig(data.config);
      setTabChanges(prev => ({ ...prev, [tabKey]: false }));
      setAutomationClearFlags({ clearPassword: false, clearUsername: false });
      setSnackbar({ open: true, message: `${getTabDisplayName(tabKey)} saved successfully!`, severity: 'success' });
    } catch (err) {
      reportSettingsError(err, { unsavedChanges: true });
    } finally {
      setSaving(false);
    }
  };

  const getTabDisplayName = (tabKey) => {
    const names = {
      'project-basics': 'Project Basics',
      'application-profile': 'Application Profile',
      'automation': 'Automation',
      'excel-import': 'Excel Import',
      'tokens-security': 'Tokens & Security',
      'advanced': 'Advanced',
      'module-taxonomy': 'Module Taxonomy'
    };
    return names[tabKey] || 'Configuration';
  };

  const handleTabChange = (newTab) => {
    const currentTabKey = getTabKeyFromConfigKey(Object.keys(config || {})[0] || '');
    if (tabChanges[currentTabKey]) {
      if (window.confirm('You have unsaved changes. Do you want to save before switching tabs?')) {
        handleTabSave(currentTabKey);
      }
    }
    setActiveTab(newTab);
  };

  // Validation functions
  const validateJiraUrl = (url) => {
    if (!url) return true; // Optional field
    const jiraPattern = /^https:\/\/[a-zA-Z0-9-]+\.atlassian\.net\/?$/;
    return jiraPattern.test(url);
  };

  const validateProjectName = (name) => {
    return name && name.trim().length > 0;
  };

  const validateEmail = (email) => {
    if (!email) return true; // Optional field
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  // Token utility functions
  const buildMaskedDisplay = (hasToken, suffix, tokenType) => {
    if (!hasToken || !suffix) return '';
    
    // Build masked display from suffix and token type
    switch (tokenType) {
      case 'openai':
        return `sk-...${suffix}`;
      case 'jira':
        return `ATATT...${suffix}`;
      case 'figma':
        return `encrypted:...${suffix}`;
      default:
        return `••••••${suffix}`;
    }
  };

  const copyToken = (token) => {
    // Note: We don't have the full token, so copy is disabled
    setSnackbar({ open: true, message: 'Token cannot be copied for security reasons', severity: 'info' });
  };


  // Environment management
  const addEnvironment = () => {
    const newEnv = {
      id: Date.now(),
      name: '',
      base_url: '',
      app_id: '',
      active: true
    };
    setEnvironments(prev => [...prev, newEnv]);
  };

  const removeEnvironment = (id) => {
    setEnvironments(prev => prev.filter(env => env.id !== id));
  };

  const updateEnvironment = (id, field, value) => {
    setEnvironments(prev => prev.map(env => 
      env.id === id ? { ...env, [field]: value } : env
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      // Only send changed values
      const updates = {};
      Object.keys(config).forEach(key => {
        if (config[key] !== originalConfig[key]) {
          updates[key] = config[key];
        }
      });
      
      if (Object.keys(updates).length === 0) {
        setSnackbar({ open: true, message: 'No changes to save', severity: 'info' });
        return;
      }
      
      const token = localStorage.getItem('stratovai_access_token');
      // Get selected project - project_id is required
      const selectedProjectStr = localStorage.getItem('selectedProject');
      const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
      const projectId = selectedProject?.project_id || selectedProject?.id;
      
      if (!projectId) {
        throw new Error('No project selected. Please select a project first.');
      }
      
      const configUrl = `${API_CONFIG.BASE_URL}/api/config?project_id=${encodeURIComponent(projectId)}`;
      const response = await fetch(configUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw createSettingsApiError(response.status, errorData.detail, 'Failed to save configuration');
      }
      
      const data = await response.json();
      setConfig(data.config);
      setOriginalConfig(data.config);
      
      // Reset API key inputs after save (never keep full keys in frontend)
      setApiKeyInputs({
        ai_api_key: '',
        jira_api_token: '',
        figma_api_token: ''
      });
      setShowKeys({
        ai_api_key: false,
        jira_api_token: false,
        figma_api_token: false
      });
      
      setSnackbar({ open: true, message: 'Configuration saved successfully!', severity: 'success' });
    } catch (err) {
      reportSettingsError(err, { unsavedChanges: true });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('stratovai_access_token');
      // Get selected project - project_id is required
      const selectedProjectStr = localStorage.getItem('selectedProject');
      const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
      const projectId = selectedProject?.project_id || selectedProject?.id;
      
      if (!projectId) {
        throw new Error('No project selected. Please select a project first.');
      }
      
      const configUrl = `${API_CONFIG.BASE_URL}/api/config/reset?project_id=${encodeURIComponent(projectId)}`;
      const response = await fetch(configUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ keys: null })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw createSettingsApiError(response.status, errorData.detail, 'Failed to reset configuration');
      }
      
      const data = await response.json();
      setConfig(data.config);
      setOriginalConfig(data.config);
      
      // Reset API key inputs after reset (never keep full keys in frontend)
      setApiKeyInputs({
        ai_api_key: '',
        jira_api_token: '',
        figma_api_token: ''
      });
      setShowKeys({
        ai_api_key: false,
        jira_api_token: false,
        figma_api_token: false
      });
      
      setSnackbar({ open: true, message: 'Configuration reset to defaults', severity: 'success' });
    } catch (err) {
      reportSettingsError(err, { unsavedChanges: true });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setConfig({ ...originalConfig });
    setSnackbar({ open: true, message: 'Changes discarded', severity: 'info' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!config || !schema) {
    return (
      <Alert severity="error">
        Failed to load configuration. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Configuration Settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfig}
            disabled={loading || saving}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ResetIcon />}
            onClick={handleReset}
            disabled={loading || saving}
          >
            Reset to Defaults
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              You have unsaved changes
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleDiscard} disabled={saving}>
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Current Config Badge */}
      <Paper sx={{ mb: 2, p: 2, backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Current Configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`High Threshold: ${config['impact.threshold_high'] ?? 'Default'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Candidate Cap: ${config['impact.candidate_cap'] ?? 'Default'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Chat Approved Only: ${config['chat.approved_only'] ? 'Yes' : 'No'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => handleTabChange(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Project Basics" />
          <Tab label="Application Profile" />
          <Tab label="Automation" data-testid="configuration-tab-automation" />
          <Tab label="Environments" />
          <Tab label="Excel Import" />
          <Tab label="Tokens & Security" />
          <Tab label="Advanced" />
          <Tab label="Module Taxonomy" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Project Basics Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Project Basics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure basic project information and JIRA integration
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 3 }}>
                <TextField
                  label="Project Name"
                  value={config['project.name'] || ''}
                  onChange={(e) => handleConfigChange('project.name', e.target.value)}
                  fullWidth
                  required
                  error={!validateProjectName(config['project.name'])}
                  helperText={!validateProjectName(config['project.name']) ? 'Project name is required' : ''}
                />

                <TextField
                  label="JIRA Project Keys"
                  value={config['project.jira_project_keys'] || ''}
                  onChange={(e) => handleConfigChange('project.jira_project_keys', e.target.value)}
                  fullWidth
                  placeholder="e.g., PROJ1, PROJ2, PROJ3"
                  helperText="Comma-separated JIRA project keys (e.g., PROJ1, PROJ2, PROJ3)"
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 3 }}>
                <TextField
                  label="JIRA Base URL"
                  value={config['project.jira_base_url'] || ''}
                  onChange={(e) => handleConfigChange('project.jira_base_url', e.target.value)}
                  fullWidth
                  placeholder="https://stratovai.atlassian.net"
                  error={!validateJiraUrl(config['project.jira_base_url'])}
                  helperText={!validateJiraUrl(config['project.jira_base_url']) ? 'Must be a valid Atlassian URL' : 'Base URL for JIRA instance (e.g., https://stratovai.atlassian.net)'}
                />

                <TextField
                  label="Project Owner Email"
                  value={config['project.owner_email'] || ''}
                  onChange={(e) => handleConfigChange('project.owner_email', e.target.value)}
                  fullWidth
                  type="email"
                  placeholder="owner@company.com"
                  error={!validateEmail(config['project.owner_email'])}
                  helperText={!validateEmail(config['project.owner_email']) ? 'Invalid email format' : 'For approvals and alerts'}
                />
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('project-basics')}
                  disabled={saving || !tabChanges['project-basics']}
                >
                  {saving ? 'Saving...' : 'Save Project Basics'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Application Profile Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Application Profile
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure application templates and target platforms
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Application Templates</InputLabel>
                  <Select
                    multiple
                    value={config['application.templates'] || ['E-commerce']}
                    onChange={(e) => handleConfigChange('application.templates', e.target.value)}
                    label="Application Templates"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="E-commerce">E-commerce</MenuItem>
                    <MenuItem value="Education">Education</MenuItem>
                    <MenuItem value="Healthcare">Healthcare</MenuItem>
                    <MenuItem value="Finance">Finance</MenuItem>
                    <MenuItem value="SaaS">SaaS</MenuItem>
                    <MenuItem value="Social Media">Social Media</MenuItem>
                    <MenuItem value="Content Management">Content Management</MenuItem>
                    <MenuItem value="CRM">CRM</MenuItem>
                    <MenuItem value="HR Management">HR Management</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Target Platforms</InputLabel>
                  <Select
                    multiple
                    value={config['application.platforms'] || ['Web']}
                    onChange={(e) => handleConfigChange('application.platforms', e.target.value)}
                    label="Target Platforms"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Web">Web</MenuItem>
                    <MenuItem value="Android">Android</MenuItem>
                    <MenuItem value="iOS">iOS</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('application-profile')}
                  disabled={saving || !tabChanges['application-profile']}
                >
                  {saving ? 'Saving...' : 'Save Application Profile'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Automation Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Automation Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure automation framework and language preferences
              </Typography>

              {frameworkSettingsStatus?.framework_mismatch && frameworkSettingsStatus?.warning_message ? (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  data-testid="automation-framework-mismatch-warning"
                >
                  {frameworkSettingsStatus.warning_message}
                </Alert>
              ) : null}

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Automation Framework</InputLabel>
                  <Select
                    value={config['automation.framework'] || 'Playwright'}
                    onChange={(e) => handleConfigChange('automation.framework', e.target.value)}
                    label="Automation Framework"
                  >
                    <MenuItem value="Playwright">Playwright</MenuItem>
                    <MenuItem value="Cypress">Cypress</MenuItem>
                    <MenuItem value="Selenium">Selenium</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Programming Language</InputLabel>
                  <Select
                    value={config['automation.language'] || 'TypeScript/JavaScript'}
                    onChange={(e) => handleConfigChange('automation.language', e.target.value)}
                    label="Programming Language"
                  >
                    <MenuItem value="TypeScript/JavaScript">TypeScript/JavaScript</MenuItem>
                    <MenuItem value="Python">Python</MenuItem>
                    <MenuItem value="Java">Java</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <AutomationRepoConfigSection
                repoConfig={automationRepoConfig}
                tokenInput={repoTokenInput}
                onRepoConfigChange={handleRepoConfigChange}
                onTokenInputChange={handleRepoTokenInputChange}
                disabled={saving}
              />

              <AutomationKnowledgePullSection
                projectId={currentProjectId}
                disabled={saving}
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" gutterBottom>
                Automation Runtime (Execution)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Credentials are encrypted and used only by the Automation Execution Worker.
                Manual test-case passwords are never used for execution.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(config['automation.execution_enabled'])}
                    onChange={(e) => handleConfigChange('automation.execution_enabled', e.target.checked)}
                  />
                }
                label="Enable automated execution"
                sx={{ mb: 2, display: 'block' }}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 3 }}>
                <TextField
                  label="Environment name"
                  value={config['automation.runtime.environment_name'] || 'test'}
                  onChange={(e) => handleConfigChange('automation.runtime.environment_name', e.target.value)}
                  fullWidth
                  helperText="Label for the configured runtime (v1: single environment per project)"
                />
                <TextField
                  label="Base URL"
                  value={config['automation.runtime.base_url'] || ''}
                  onChange={(e) => handleConfigChange('automation.runtime.base_url', e.target.value)}
                  fullWidth
                  placeholder="https://test-stratovai.netlify.app/app"
                  helperText="HTTPS target for Playwright execution (server-validated)"
                />
                <TextField
                  label="Project / display name"
                  value={config['automation.runtime.project_name'] || ''}
                  onChange={(e) => handleConfigChange('automation.runtime.project_name', e.target.value)}
                  fullWidth
                  placeholder="Automation Testing"
                />
                <TextField
                  label="Test username"
                  value={config['automation.runtime.username'] || ''}
                  onChange={(e) => handleConfigChange('automation.runtime.username', e.target.value)}
                  fullWidth
                  autoComplete="off"
                  helperText={
                    config['automation.runtime.has_username']
                      ? 'Username configured — enter a new value to replace'
                      : 'Stored encrypted at rest'
                  }
                />
                <TextField
                  label="Test password"
                  type="password"
                  value={config['automation.runtime.password'] || ''}
                  onChange={(e) => handleConfigChange('automation.runtime.password', e.target.value)}
                  fullWidth
                  autoComplete="new-password"
                  helperText={
                    config['automation.runtime.has_password']
                      ? 'Password configured — leave blank to keep existing'
                      : 'Required when execution is enabled'
                  }
                />
              </Box>

              <TextField
                label="Additional allowed hosts"
                value={
                  Array.isArray(config['automation.runtime.allowed_hosts'])
                    ? config['automation.runtime.allowed_hosts'].join(', ')
                    : (config['automation.runtime.allowed_hosts'] || '')
                }
                onChange={(e) => handleConfigChange('automation.runtime.allowed_hosts', e.target.value)}
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                placeholder="cdn.example.com, api.example.com"
                helperText="Optional public hostnames the browser may contact during execution. Base URL host is always included. No wildcards."
              />

              {config['automation.runtime.has_password'] && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  sx={{ mb: 2 }}
                  onClick={() => {
                    if (!window.confirm('Clear stored automation password?')) return;
                    setAutomationClearFlags((prev) => ({ ...prev, clearPassword: true }));
                    setTabChanges((prev) => ({ ...prev, automation: true }));
                  }}
                >
                  Clear stored password
                </Button>
              )}

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('automation')}
                  disabled={saving || (!tabChanges['automation'] && !repoConfigChanged)}
                >
                  {saving ? 'Saving...' : 'Save Automation'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Environments Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Environment Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure testing environments with base URLs and settings
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Base URL / App ID</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {environments.map((env) => (
                      <TableRow key={env.id}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={env.name}
                            onChange={(e) => updateEnvironment(env.id, 'name', e.target.value)}
                            placeholder="e.g., Dev, QA, Staging"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={env.base_url}
                            onChange={(e) => updateEnvironment(env.id, 'base_url', e.target.value)}
                            placeholder="https://dev.example.com or com.example.app"
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={env.active}
                            onChange={(e) => updateEnvironment(env.id, 'active', e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => removeEnvironment(env.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                startIcon={<AddIcon />}
                onClick={addEnvironment}
                sx={{ mt: 2 }}
                variant="outlined"
              >
                Add Environment
              </Button>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('environments')}
                  disabled={saving || !tabChanges['environments']}
                >
                  {saving ? 'Saving...' : 'Save Environments'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Excel Import Tab */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Excel Import Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure Excel import settings and column mappings
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 3 }}>
                <TextField
                  label="Excel Sheet Name"
                  value={config['excel.sheet_name'] || 'TestCases'}
                  onChange={(e) => handleConfigChange('excel.sheet_name', e.target.value)}
                  fullWidth
                  helperText="Name of the sheet containing test cases"
                />

                <FormControl fullWidth>
                  <InputLabel>Upsert Key</InputLabel>
                  <Select
                    value={config['excel.upsert_key'] || 'title_module'}
                    onChange={(e) => handleConfigChange('excel.upsert_key', e.target.value)}
                    label="Upsert Key"
                  >
                    <MenuItem value="title_module">Title + Module</MenuItem>
                    <MenuItem value="external_id">External ID</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('excel-import')}
                  disabled={saving || !tabChanges['excel-import']}
                >
                  {saving ? 'Saving...' : 'Save Excel Import'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Tokens & Security Tab */}
          {activeTab === 5 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Tokens & Security
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage API tokens and security settings
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  AI API Key
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="text"
                    value={showKeys.ai_api_key 
                      ? apiKeyInputs.ai_api_key
                      : (config['tokens.has_ai_api_key'] 
                          ? buildMaskedDisplay(config['tokens.has_ai_api_key'], config['tokens.ai_api_key_suffix'], 'openai')
                          : '')
                    }
                    onChange={(e) => {
                      // Update local input state
                      setApiKeyInputs({ ...apiKeyInputs, ai_api_key: e.target.value });
                      // Update config for save
                      handleConfigChange('tokens.ai_api_key', e.target.value);
                    }}
                    placeholder={
                      config['tokens.ai_api_key_source'] === 'default' 
                        ? "Using default API key (leave empty to continue using default)"
                        : "Enter your OpenAI API key"
                    }
                    fullWidth
                    InputProps={{
                      startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => setShowKeys({ ...showKeys, ai_api_key: !showKeys.ai_api_key })}
                            disabled={!config['tokens.has_ai_api_key']}
                          >
                            {showKeys.ai_api_key ? <HideIcon /> : <ShowIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& input': {
                        fontFamily: showKeys.ai_api_key ? 'inherit' : 'monospace',
                        letterSpacing: showKeys.ai_api_key ? 'normal' : '0.1em'
                      }
                    }}
                  />
                  <Button
                    startIcon={<CopyIcon />}
                    onClick={() => copyToken(config['tokens.ai_api_key'])}
                    variant="outlined"
                    disabled={true}
                    title="Token cannot be copied for security reasons"
                  >
                    Copy
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {config['tokens.ai_api_key_source'] === 'default' 
                    ? "Currently using Stratovai's default API key. Enter your own key to use your account, or leave empty to continue using the default."
                    : "Enter your OpenAI API key. It will be stored securely. Leave empty to use Stratovai's default key."}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  JIRA API Token
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="text"
                    value={showKeys.jira_api_token 
                      ? apiKeyInputs.jira_api_token
                      : (config['tokens.has_jira_api_token'] 
                          ? buildMaskedDisplay(config['tokens.has_jira_api_token'], config['tokens.jira_api_token_suffix'], 'jira')
                          : '')
                    }
                    onChange={(e) => {
                      // Update local input state
                      setApiKeyInputs({ ...apiKeyInputs, jira_api_token: e.target.value });
                      // Update config for save
                      handleConfigChange('tokens.jira_api_token', e.target.value);
                    }}
                    placeholder="Enter your JIRA API token"
                    fullWidth
                    InputProps={{
                      startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => setShowKeys({ ...showKeys, jira_api_token: !showKeys.jira_api_token })}
                            disabled={!config['tokens.has_jira_api_token']}
                          >
                            {showKeys.jira_api_token ? <HideIcon /> : <ShowIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& input': {
                        fontFamily: showKeys.jira_api_token ? 'inherit' : 'monospace',
                        letterSpacing: showKeys.jira_api_token ? 'normal' : '0.1em'
                      }
                    }}
                  />
                  <Button
                    startIcon={<CopyIcon />}
                    onClick={() => copyToken(config['tokens.jira_api_token'])}
                    variant="outlined"
                    disabled={true}
                    title="Token cannot be copied for security reasons"
                  >
                    Copy
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Enter your JIRA API token. It will be encrypted and stored securely in the database.
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  JIRA Email
                </Typography>
                <TextField
                  type="email"
                  value={config['tokens.jira_email'] || ''}
                  onChange={(e) => handleConfigChange('tokens.jira_email', e.target.value)}
                  placeholder="Enter your JIRA email/username"
                  fullWidth
                  error={config['tokens.jira_email'] && !validateEmail(config['tokens.jira_email'])}
                  helperText={
                    config['tokens.jira_email'] && !validateEmail(config['tokens.jira_email'])
                      ? 'Invalid email format'
                      : 'Email address used for JIRA authentication (e.g., your-email@company.com)'
                  }
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Figma API Token
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="text"
                    value={showKeys.figma_api_token 
                      ? apiKeyInputs.figma_api_token
                      : (config['tokens.has_figma_api_token'] 
                          ? buildMaskedDisplay(config['tokens.has_figma_api_token'], config['tokens.figma_api_token_suffix'], 'figma')
                          : '')
                    }
                    onChange={(e) => {
                      // Update local input state
                      setApiKeyInputs({ ...apiKeyInputs, figma_api_token: e.target.value });
                      // Update config for save
                      handleConfigChange('tokens.figma_api_token', e.target.value);
                    }}
                    placeholder="Enter your Figma API token"
                    fullWidth
                    InputProps={{
                      startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => setShowKeys({ ...showKeys, figma_api_token: !showKeys.figma_api_token })}
                            disabled={!config['tokens.has_figma_api_token']}
                          >
                            {showKeys.figma_api_token ? <HideIcon /> : <ShowIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& input': {
                        fontFamily: showKeys.figma_api_token ? 'inherit' : 'monospace',
                        letterSpacing: showKeys.figma_api_token ? 'normal' : '0.1em'
                      }
                    }}
                  />
                  <Button
                    startIcon={<CopyIcon />}
                    onClick={() => copyToken(config['tokens.figma_api_token'])}
                    variant="outlined"
                    disabled={true}
                    title="Token cannot be copied for security reasons"
                  >
                    Copy
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Enter your Figma API token. It will be encrypted and stored securely in the database. Leave blank if not available.
                </Typography>
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('tokens-security')}
                  disabled={saving || !tabChanges['tokens-security']}
                >
                  {saving ? 'Saving...' : 'Save Tokens & Security'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Advanced Tab */}
          {activeTab === 6 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Advanced Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Legacy settings and advanced options
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography gutterBottom>
                  High Confidence Threshold: <strong>{config['impact.threshold_high'] || 0.8}</strong>
                </Typography>
                <Slider
                  value={config['impact.threshold_high'] || 0.8}
                  onChange={(e, value) => handleConfigChange('impact.threshold_high', value)}
                  min={0.1}
                  max={1.0}
                  step={0.01}
                  marks={[
                    { value: 0.2, label: '0.2' },
                    { value: 0.4, label: '0.4' },
                    { value: 0.6, label: '0.6' },
                    { value: 0.8, label: '0.8' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config['chat.approved_only'] || false}
                      onChange={(e) => handleConfigChange('chat.approved_only', e.target.checked)}
                    />
                  }
                  label="Use Approved Content Only"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  Candidate Cap: <strong>{config['impact.candidate_cap'] || 50}</strong>
                </Typography>
                <Slider
                  value={config['impact.candidate_cap'] || 50}
                  onChange={(e, value) => handleConfigChange('impact.candidate_cap', value)}
                  min={10}
                  max={200}
                  step={5}
                  marks={[
                    { value: 10, label: '10' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                    { value: 200, label: '200' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Premium model escalation for expensive operations (admin only) */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {ADVANCED_AI_ESCALATION_COPY.sectionTitle}
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" component="span" display="block">
                    {ADVANCED_AI_ESCALATION_COPY.alertSummary}
                  </Typography>
                  <Typography variant="body2" component="span" display="block" sx={{ mt: 1 }}>
                    {ADVANCED_AI_ESCALATION_COPY.helperFull}
                  </Typography>
                  <Typography variant="caption" component="span" display="block" sx={{ mt: 1 }} color="inherit">
                    {ADVANCED_AI_ESCALATION_COPY.projectScopedAdminNote}
                  </Typography>
                </Alert>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config['ai.use_gpt_5_2'] || false}
                        onChange={async (e) => {
                          try {
                            const enabled = Boolean(e?.target?.checked);
                            const token = localStorage.getItem('stratovai_access_token');
                            
                            // Get selected project - project_id is required
                            const selectedProjectStr = localStorage.getItem('selectedProject');
                            const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
                            const projectId = selectedProject?.project_id || selectedProject?.id;
                            
                            if (!projectId) {
                              throw new Error('No project selected. Please select a project first.');
                            }
                            
                            // PATCH project config (admin-only) - this is the single source of truth
                            const configUrl = `${API_CONFIG.BASE_URL}/api/config?project_id=${encodeURIComponent(projectId)}`;
                            const response = await fetch(configUrl, {
                              method: 'PATCH',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ updates: { 'ai.use_gpt_5_2': enabled } })
                            });
                            
                            if (!response.ok) {
                              const errorData = await response.json().catch(() => ({}));
                              throw new Error(errorData.detail || ADVANCED_AI_ESCALATION_COPY.errorToggle);
                            }
                            
                            const data = await response.json();
                            if (data?.config) {
                              setConfig(data.config);
                              setOriginalConfig(data.config);
                              setTabChanges(prev => ({ ...prev, advanced: false }));
                            } else {
                              // Defensive fallback if API shape changes
                              setConfig({ ...config, 'ai.use_gpt_5_2': enabled });
                              setOriginalConfig({ ...(originalConfig || {}), 'ai.use_gpt_5_2': enabled });
                            }
                            setSnackbar({
                              open: true,
                              message: enabled
                                ? ADVANCED_AI_ESCALATION_COPY.snackbarEnabled
                                : ADVANCED_AI_ESCALATION_COPY.snackbarDisabled,
                              severity: 'success'
                            });
                          } catch (err) {
                            setSnackbar({
                              open: true,
                              message: err.message || ADVANCED_AI_ESCALATION_COPY.errorToggle,
                              severity: 'error'
                            });
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography>{ADVANCED_AI_ESCALATION_COPY.toggleLabel}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatEscalationPremiumCaption(config)}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('advanced')}
                  disabled={saving || !tabChanges['advanced']}
                >
                  {saving ? 'Saving...' : 'Save Advanced'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Module Taxonomy Tab */}
          {activeTab === 7 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Module Taxonomy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure module taxonomy for automatic classification of test cases, JIRA issues, and Figma designs
              </Typography>

              {/* Configuration Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Classification Settings
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config['module_taxonomy.enabled'] !== false}
                        onChange={(e) => handleConfigChange('module_taxonomy.enabled', e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography>Enable Module Taxonomy</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatically classify items into modules
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControl fullWidth>
                    <InputLabel>Classification Mode</InputLabel>
                    <Select
                      value={config['module_taxonomy.classification_mode'] || 'llm_with_taxonomy'}
                      label="Classification Mode"
                      onChange={(e) => handleConfigChange('module_taxonomy.classification_mode', e.target.value)}
                    >
                      <MenuItem value="off">Off (Disabled)</MenuItem>
                      <MenuItem value="rules_only">Rules Only (JIRA components/labels)</MenuItem>
                      <MenuItem value="llm_with_taxonomy">LLM with Taxonomy</MenuItem>
                      <MenuItem value="llm_allow_suggest">LLM with Suggestions</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config['module_taxonomy.allow_autocreate'] === true}
                        onChange={(e) => handleConfigChange('module_taxonomy.allow_autocreate', e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography>Auto-Create Modules</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatically create new modules from LLM suggestions
                        </Typography>
                      </Box>
                    }
                  />

                  <TextField
                    label="Max Modules Per Item"
                    type="number"
                    value={config['module_taxonomy.max_modules_per_item'] || 3}
                    onChange={(e) => handleConfigChange('module_taxonomy.max_modules_per_item', parseInt(e.target.value) || 3)}
                    inputProps={{ min: 1, max: 5 }}
                    fullWidth
                    helperText="Maximum number of modules that can be assigned to a single item (1-5)"
                  />

                  <TextField
                    label="Per-Import Budget (USD)"
                    type="number"
                    value={config['module_taxonomy.per_import_budget_usd'] || 10.0}
                    onChange={(e) => handleConfigChange('module_taxonomy.per_import_budget_usd', parseFloat(e.target.value) || 10.0)}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    fullWidth
                    helperText="Maximum budget per import for LLM classification"
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Module List */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Modules ({modules.length})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingModule(null);
                      setModuleForm({ display_name: '', description: '', aliases: '' });
                      setModuleDialogOpen(true);
                    }}
                    size="small"
                  >
                    Add Module
                  </Button>
                </Box>

                {loadingModules ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : modules.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No modules found. Add your first module to get started.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Display Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Module Key</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Aliases</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Usage</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {modules.map((module) => (
                          <TableRow key={module.id}>
                            <TableCell>{module.display_name}</TableCell>
                            <TableCell>
                              <Chip label={module.module_key} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              {module.aliases && module.aliases.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {module.aliases.slice(0, 3).map((alias, idx) => (
                                    <Chip key={idx} label={alias} size="small" />
                                  ))}
                                  {module.aliases.length > 3 && (
                                    <Chip label={`+${module.aliases.length - 3}`} size="small" />
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>{module.usage_count || 0}</TableCell>
                            <TableCell>
                              <Chip
                                label={module.created_by === 'user' ? 'User' : 'Auto'}
                                size="small"
                                color={module.created_by === 'user' ? 'primary' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={module.is_active ? 'Active' : 'Inactive'}
                                size="small"
                                color={module.is_active ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingModule(module);
                                  setModuleForm({
                                    display_name: module.display_name,
                                    description: module.description || '',
                                    aliases: (module.aliases || []).join(', ')
                                  });
                                  setModuleDialogOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTabSave('module-taxonomy')}
                  disabled={saving || !tabChanges['module-taxonomy']}
                >
                  {saving ? 'Saving...' : 'Save Module Taxonomy'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Audit Footer */}
      {config && (
        <Paper sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {new Date(config.updated_at || Date.now()).toLocaleString()} • 
            Edited by: {config.updated_by || 'System'}
          </Typography>
        </Paper>
      )}

      {/* Module Dialog */}
      <Dialog open={moduleDialogOpen} onClose={() => setModuleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingModule ? 'Edit Module' : 'Add Module'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Display Name"
              value={moduleForm.display_name}
              onChange={(e) => setModuleForm({ ...moduleForm, display_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={moduleForm.description}
              onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Aliases (comma-separated)"
              value={moduleForm.aliases}
              onChange={(e) => setModuleForm({ ...moduleForm, aliases: e.target.value })}
              fullWidth
              helperText="Alternative names for this module, separated by commas"
              placeholder="e.g., Login, Sign-in, Authentication"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                const token = localStorage.getItem('stratovai_access_token');
                const selectedProjectStr = localStorage.getItem('selectedProject');
                const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
                const projectId = selectedProject?.project_id || selectedProject?.id;
                
                if (!projectId) {
                  throw new Error('No project selected');
                }
                
                const moduleKey = (moduleForm.display_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
                const aliasesList = moduleForm.aliases.split(',').map(a => a.trim()).filter(a => a);
                
                if (editingModule) {
                  // Update module
                  const updateUrl = `${API_CONFIG.BASE_URL}/api/module-taxonomy/${projectId}/modules/${editingModule.module_key}`;
                  await fetch(updateUrl, {
                    method: 'PATCH',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      display_name: moduleForm.display_name,
                      description: moduleForm.description || null,
                      aliases: aliasesList
                    })
                  });
                } else {
                  // Create module
                  const createUrl = `${API_CONFIG.BASE_URL}/api/module-taxonomy/${projectId}/modules`;
                  await fetch(createUrl, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      module_key: moduleKey,
                      display_name: moduleForm.display_name,
                      description: moduleForm.description || null,
                      aliases: aliasesList
                    })
                  });
                }
                
                setModuleDialogOpen(false);
                fetchModules();
                setSnackbar({
                  open: true,
                  message: editingModule ? 'Module updated successfully' : 'Module created successfully',
                  severity: 'success'
                });
              } catch (err) {
                setSnackbar({
                  open: true,
                  message: err.message || 'Failed to save module',
                  severity: 'error'
                });
              }
            }}
            disabled={!moduleForm.display_name}
          >
            {editingModule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigurationSettings;

